import React, { useContext, useEffect, useCallback } from 'react';
import {
	CreateCustomerContext,
	ICreateCustomerContext,
	createCustomerInitialState,
} from '../../createCustomerState/createCustomerContext';
import { Form } from 'antd';
import { FormPage } from 'components/EntityForm/FormPage';
import { FormSection } from 'components/EntityForm/FormSection';
import { FormRow } from 'components/EntityForm/FormRow';
import { FormColumn } from 'components/EntityForm/FormColumn';
import { FormActions } from 'components/EntityForm/FormActions';
import { ImageBlock } from 'components/EntityForm/ImageBlock';
import { IconName } from 'components/Icon/Icon';
import { BillTemplateBlockNew } from 'components/BillTemplateBlock/BillTemplateBlockNew';
import { TipsBlockNew } from 'components/TipsBlock/TipsBlockNew';
import { NameField } from './NameField';
import { EmailField } from './EmailField';
import { EmailBodyTemplateField } from './EmailBodyTemplateField';
import { ClientTypeSelect } from './ClientTypeSelect';
import { PriceTypeField } from './PriceTypeField';
import { AdsField } from './AdsField';
import { DefaultLanguageSelect } from './DefaultLanguageSelect';
import { LanguagesMultipleSelect } from './LanguagesMultipleSelect';
import { StopListSyncIntervalField } from './StopListSyncIntervalField';
import { MixpanelTokenField } from './MixpanelTokenField';

export const CustomerForm: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeName,
		changeEmail,
		changeEmailBodyTemplate,
		setClientType,
		setTips0,
		setTips1,
		setTips2,
		setTips3,
		setDefaultTips,
		changeBillTemplateFileData,
		changeBillAdvertisingImageData,
		changeBillAdvertisingLink,
		changePriceType,
		setAds,
		setMixpanelToken,
		setStopListSyncIntervalMinutes,
		setCustomerDefaultLanguage,
		setCustomerLanguages,
		setFetchedLanguages,
		setFile,
		fileId,
		sourceUrlLocally,
		name,
		email,
		emailBodyTemplate,
		clientType,
		billTemplateFileData,
		billAdvertisingImageData,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		defaultTips,
		ads,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
	} = useContext<ICreateCustomerContext>(CreateCustomerContext);
	const [form] = Form.useForm();

	const hasChanges = !!(
		fileId ||
		name ||
		email ||
		emailBodyTemplate ||
		clientType ||
		billTemplateFileData.fileId ||
		billAdvertisingImageData.fileId ||
		billAdvertisingLink ||
		priceType ||
		tips0 ||
		tips1 ||
		tips2 ||
		tips3 ||
		defaultTips ||
		ads ||
		mixpanelToken ||
		stopListSyncIntervalMinutes !== createCustomerInitialState.stopListSyncIntervalMinutes ||
		IDSelectedCustomerDefaultLanguage ||
		IDsSelectedCustomerLanguages.length
	);

	useEffect(() => {
		form.setFieldsValue({
			name,
			email,
			emailBodyTemplate,
			clientType,
			billAdvertisingLink,
			priceType,
			tips0,
			tips1,
			tips2,
			tips3,
			ads,
			mixpanelToken,
			stopListSyncIntervalMinutes,
			IDSelectedCustomerDefaultLanguage,
			IDsSelectedCustomerLanguages,
		});
	}, [
		name,
		email,
		emailBodyTemplate,
		clientType,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		ads,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
	]);

	const submit = async () => {
		setDataForSubmit({
			fileId,
			name,
			email,
			emailBodyTemplate,
			clientType: clientType || 1,
			billTemplateId: billTemplateFileData.fileId,
			billAdvertisingImage: billAdvertisingImageData.fileId,
			billAdvertisingLink,
			priceType,
			defaultTips: [
				{ value: tips0, isDefault: defaultTips === '0' },
				{ value: tips1, isDefault: defaultTips === '1' },
				{ value: tips2, isDefault: defaultTips === '2' },
				{ value: tips3, isDefault: defaultTips === '3' },
			],
			ads: Number(ads),
			mixpanelToken: mixpanelToken || null,
			stopListSyncIntervalMinutes: stopListSyncIntervalMinutes || 0,
			defaultLanguageId: IDSelectedCustomerDefaultLanguage || '',
			availableLanguageIds: IDsSelectedCustomerLanguages,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleCancel = () => {
		changeTypeOfModalWindow('toConfirmFieldsClearing');
	};

	const handleUploadPhoto = ({ fileId, imageSrc }: { fileId: string; imageSrc: string }) => {
		setFile(fileId, imageSrc);
	};

	const handleDeletePhoto = () => {
		setFile(null, null);
	};

	const handleInvalidImageFormat = () => {
		changeTypeOfModalWindow('invalidFileFormat');
	};

	const handleInvalidHtmlFormat = () => {
		changeTypeOfModalWindow('invalidHtmlFileFormat');
	};

	const handleFileUploadError = () => {
		changeTypeOfModalWindow('fileUploadError');
	};

	const handleTooManyFiles = () => {
		changeTypeOfModalWindow('tooManyFiles');
	};

	const handleImageFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('imageFileSizeExceeded'), []);
	const handleHtmlFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('htmlFileSizeExceeded'), []);

	const handleDeleteBillAdvertisingImage = useCallback(
		() =>
			changeBillAdvertisingImageData({
				fileId: '',
				fileHash: null,
				fileName: null,
			}),
		[],
	);

	return (
		<FormPage form={form} testId="Form" onFinish={submit}>
			<FormSection translationKey="mainInfoHeading" testId="MainInfoSection">
				<FormRow>
					<ImageBlock
						imageSrc={sourceUrlLocally}
						placeholderIcon={IconName.user}
						maxFileSizeMB={1}
						onUpload={handleUploadPhoto}
						onDelete={handleDeletePhoto}
						onSizeExceeded={handleImageFileSizeExceeded}
						onInvalidFormat={handleInvalidImageFormat}
						onUploadError={handleFileUploadError}
					/>
					<FormColumn>
						<NameField changeName={changeName} />
						<EmailBodyTemplateField changeEmailBodyTemplate={changeEmailBodyTemplate} />
					</FormColumn>
					<FormColumn>
						<EmailField changeEmail={changeEmail} />
						<ClientTypeSelect setClientType={setClientType} />
					</FormColumn>
				</FormRow>
			</FormSection>
			<FormSection translationKey="billTemplateAndAdImageHeading" testId="BillTemplateSection">
				<BillTemplateBlockNew
					billTemplateFileData={billTemplateFileData}
					billAdvertisingImageData={billAdvertisingImageData}
					changeBillTemplateFileData={changeBillTemplateFileData}
					changeBillAdvertisingImageData={changeBillAdvertisingImageData}
					changeBillAdvertisingLink={changeBillAdvertisingLink}
					onDeleteBillAdvertisingImage={handleDeleteBillAdvertisingImage}
					onHtmlFileSizeExceeded={handleHtmlFileSizeExceeded}
					onImageFileSizeExceeded={handleImageFileSizeExceeded}
					onInvalidHtmlFormat={handleInvalidHtmlFormat}
					onInvalidImageFormat={handleInvalidImageFormat}
					onUploadError={handleFileUploadError}
					onTooManyFiles={handleTooManyFiles}
				/>
			</FormSection>
			<FormSection translationKey="priceTypeHeading" testId="PriceTypeSection">
				<PriceTypeField changePriceType={changePriceType} />
			</FormSection>
			<FormSection translationKey="tipsHeading" testId="TipsSection">
				<TipsBlockNew
					setTips0={setTips0}
					setTips1={setTips1}
					setTips2={setTips2}
					setTips3={setTips3}
					setDefaultTips={setDefaultTips}
					defaultTips={defaultTips}
				/>
			</FormSection>
			<FormSection translationKey="adsHeading" testId="AdsSection">
				<AdsField setAds={setAds} />
			</FormSection>
			<FormSection translationKey="languagesHeading" testId="LanguagesSection">
				<FormRow>
					<DefaultLanguageSelect
						setCustomerDefaultLanguage={setCustomerDefaultLanguage}
						setFetchedLanguages={setFetchedLanguages}
					/>
					<LanguagesMultipleSelect
						IDSelectedCustomerDefaultLanguage={IDSelectedCustomerDefaultLanguage}
						IDsSelectedCustomerLanguages={IDsSelectedCustomerLanguages}
						setCustomerLanguages={setCustomerLanguages}
					/>
				</FormRow>
			</FormSection>
			<FormSection translationKey="enableStopListHeading" testId="StopListSection">
				<StopListSyncIntervalField setStopListSyncIntervalMinutes={setStopListSyncIntervalMinutes} />
			</FormSection>
			<FormSection translationKey="mixpanelHeading" testId="MixpanelSection">
				<MixpanelTokenField setMixpanelToken={setMixpanelToken} />
			</FormSection>
			<FormActions isSaveDisabled={!hasChanges} isCancelDisabled={!hasChanges} onCancel={handleCancel} />
		</FormPage>
	);
};
